import type {AxiosResponse} from "axios";
import {Bindings} from "data/constants/bindings";
import type {
	IAuthApiProvider,
	IUserLoginPayload,
	IUserExistsResponse,
} from "data/providers/api/auth.api.provider";
import type {
	IForgotPasswordPayload,
	IPasswordApiProvider,
	IResetPasswordPayload,
} from "data/providers/api/password.api.provider";
import type {
	IRegistrationPayload,
	IUpdateUserPayload,
	IUserApiProvider,
} from "data/providers/api/user.api.provider";
import type {ISSOPayloadWithDisplayName, ISSOPayload} from "data/utils/SSO";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable, runInAction} from "mobx";

export interface IUser {
	id: number;
	email: string;
	displayName: string;
	isNotificationsEnabled: boolean;
	isPromotionDemotionModalViewed: boolean;
	code: string;
	isTermsAccepted: boolean;
}

export interface IUserStore {
	get user(): IUser | undefined;

	get uid(): string;

	get isAuthorized(): boolean;

	get wasLoggedOut(): boolean;

	get isTermsAccepted(): boolean;

	get ssoDataForRegistration(): ISSOPayloadWithDisplayName | undefined;

	forgotPassword(payload: IForgotPasswordPayload): Promise<AxiosResponse<void>>;

	resetPassword(payload: IResetPasswordPayload): Promise<AxiosResponse<void>>;

	register(payload: IRegistrationPayload): Promise<void>;

	update(payload: IUpdateUserPayload): Promise<void>;

	deactivate(): Promise<void>;

	acceptTerms(): Promise<void>;

	login(payload: IUserLoginPayload, isRegistration?: boolean): Promise<void>;

	saveSSODataForRegistration(payload: ISSOPayloadWithDisplayName): void;

	clearSSOData(): void;

	logout(): Promise<void>;

	requestUser(): Promise<void>;

	checkUser(uuid: string): Promise<IUserExistsResponse>;

	backdoor(formData: FormData): Promise<void>;

	setPromotionAsViewed(): void;

	get ssoPersisted(): ISSOPayload | undefined;
	saveSSO(payload: ISSOPayload): void;
}

@injectable()
export class UserStore implements IUserStore {
	@observable private _ssoData?: ISSOPayloadWithDisplayName = undefined;
	@observable private _ssoPersisted?: ISSOPayload = undefined;
	@observable private _user?: IUser = undefined;
	@observable private _uid?: string = undefined;
	@observable private _wasLoggedOut = false;

	constructor(
		@inject(Bindings.AuthApiProvider) private _authApi: IAuthApiProvider,
		@inject(Bindings.UserApiProvider) private _userApi: IUserApiProvider,
		@inject(Bindings.PasswordApiProvider) private _passwordApi: IPasswordApiProvider
	) {
		makeAutoObservable(this);
	}

	public get ssoDataForRegistration(): ISSOPayloadWithDisplayName | undefined {
		return this._ssoData;
	}

	get isAuthorized() {
		return Boolean(this.user);
	}

	get wasLoggedOut() {
		return this._wasLoggedOut;
	}

	get isTermsAccepted() {
		return !!this.user?.isTermsAccepted;
	}

	get user() {
		return this._user;
	}

	get uid() {
		return this._uid || "";
	}

	@action
	async requestUser() {
		const response = await this._userApi.user();
		const {user} = response.data.success;

		runInAction(() => {
			this._user = user;
		});
	}

	@action
	async login(payload: IUserLoginPayload, isRegistration?: boolean) {
		const response = await this._authApi.login(payload);

		if (isRegistration) {
			await this.acceptTerms();
		}
		const {user} = response.data.success;

		runInAction(() => {
			this._user = user;
			this._uid = payload.uid;
			this._wasLoggedOut = false;
		});
	}

	@action
	async register(payload: IRegistrationPayload) {
		const response = await this._userApi.register(payload);
		const {user} = response.data.success;

		runInAction(() => {
			this._user = user;
			this._wasLoggedOut = false;
		});
	}

	@action
	async update(payload: IUpdateUserPayload) {
		const response = await this._userApi.update(payload);
		const {user} = response.data.success;

		runInAction(() => {
			this._user = user;
		});
	}

	@action
	async logout() {
		await this._authApi.logout();

		runInAction(() => {
			this._user = undefined;
			this._wasLoggedOut = true;
			this._uid = undefined;
			this._ssoPersisted = undefined;
		});
	}

	@action
	async deactivate() {
		await this._userApi.deactivateAccount();

		runInAction(() => {
			this._user = undefined;
			this._wasLoggedOut = true;
		});
	}

	@action
	async acceptTerms() {
		await this._userApi.acceptTerms();
		runInAction(() => {
			if (this._user) {
				this._user = {...this._user, isTermsAccepted: true};
			}
		});
	}

	forgotPassword(payload: IForgotPasswordPayload) {
		return this._passwordApi.forgotPassword(payload);
	}

	resetPassword(payload: IResetPasswordPayload) {
		return this._passwordApi.resetPassword(payload);
	}

	async backdoor(payload: FormData) {
		const response = await this._authApi.backdoor(payload);
		const {user} = response.data.success;

		runInAction(() => {
			this._user = user;
			this._wasLoggedOut = false;
		});
	}

	@action
	public setPromotionAsViewed() {
		if (this.user) {
			this._user = {...this.user, isPromotionDemotionModalViewed: true};
		}
	}

	async checkUser(uuid: string): Promise<IUserExistsResponse> {
		const {data} = await this._authApi.getIsUserExists(uuid);
		return data.success;
	}

	@action
	public clearSSOData(): void {
		this._ssoData = undefined;
	}

	@action
	public saveSSODataForRegistration(payload: ISSOPayloadWithDisplayName): void {
		this._ssoData = payload;
	}

	get ssoPersisted() {
		return this._ssoPersisted;
	}

	public saveSSO(payload: ISSOPayload) {
		this._ssoPersisted = payload;
	}
}
